import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CustomerProfile } from 'src/app/models/customer-profile';
import { ValidatorsUtil } from 'src/app/services/validators.util';
import { Countries } from 'src/app/utils/location/countries';
import { LocationService } from 'src/app/services/location.service';
import { CustomerService } from 'src/app/services/customer.service';
import { City } from 'src/app/models';
import { FileUpload } from 'primeng/fileupload';
import { SelectItem } from 'primeng/api';
import { forkJoin, Observable, of } from 'rxjs';
import { AccessGroupService } from 'src/app/services/access-controler/access-group.service';
import { AccessGroupEnum } from 'src/app/models/dtos/access-controller/access-group-enum';
import { CountryDDI, DdiCountries } from 'src/app/utils/location/ddi-countries';
import { OperationalConfigService } from 'src/app/services/config/operational-config.service';
import { GroupService } from 'src/app/services/group.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessageUtil } from 'src/app/utils/message.util';
import { FormatUtil } from 'src/app/utils/format.util';


import { CountryCode, COUNTRY_CODES, findCountryByDialCode, normalizeDialCode } from '../../../../constants/country-codes.constants';



interface CustomerPayload {
  name: string;
  nationality: string;
  accessGroupId: number;
  customerProfileId: number;
  workshop: boolean;
  federalId: string;
  federalIdType: string;
  emails: string[];
  billingEmails: string[];
  billingGroupId: number;
  billingStartDate: string; // ISO 8601 string
  dueDate: number;
  phone: string;
  phoneCountryCode: string | null;
  codRegistration?: string | null;
  photoUrl?: string | null;
  address: {
    country: string;
    zip: string;
    state: string;
    city: string;
    district: string;
    street: string;
    number: string;
    complement?: string;
    addressId?: number;
  };
}

@Component({
  selector: 'app-customer-basic-info-form',
  templateUrl: './customer-basic-info-form.component.html',
  styleUrls: ['./customer-basic-info-form.component.scss']
})
export class CustomerBasicInfoFormComponent implements OnInit{
  @ViewChild('upload', { static: false }) upload: FileUpload;

  customerForm: FormGroup;
  isBrazilianNationality = true;
  isBrazilianAddress = true;

  federalIdMask = '999.999.999-99';
  imgPreviewPhoto: string = '';
  submitted = false;
  isWorkshop: Boolean;
  isYatchClub: Boolean;


  countryCodes: CountryCode[] = COUNTRY_CODES;
  useEmailCollection = false;
  countries = Countries.countryList.map(c => {

    const countryNamesPt = {
      'Brazil': 'Brasil',
      'United States': 'Estados Unidos',
    };

    const label = countryNamesPt[c.name] || c.name;
    return { label, value: c.code };
  });
  states: { label: string; value: any }[];
  filteredCities: string[] = [];
  accessGroupOptions: SelectItem[] = [{ label: 'Selecione...', value: null }];
  customerProfiles: Array<SelectItem>;
  customerProfile: Array<any>;
  groups = [];
  availableDays = Array.from({length: 28}, (_, i) => i + 1);
  emailValues: string[] = [];
  emailCollectionValues: string[] = [];
  federalIdTypeOptions = [
    { label: 'Pessoa Física', value: 'Physical' },
    { label: 'Pessoa Jurídica', value: 'Juridical' }
  ];

  isEditMode = false;
  customerId: number = null;
  customerData: any = null;



  selectedCountry: CountryCode = this.countryCodes[0];
  phoneNumber: string = '';
  filteredCountries: Observable<CountryCode[]>;

  constructor(
    private locationService: LocationService,
    private customerService: CustomerService,
    private validatorsUtil: ValidatorsUtil,
    public dialogRef: DynamicDialogRef,
    private accessCategoryService: AccessGroupService,
    private cdRef: ChangeDetectorRef,
    private ddiCountries: DdiCountries,
    private operationalConfigService: OperationalConfigService,
    private groupService: GroupService,
    private config: DynamicDialogConfig,
    private spinner: NgxSpinnerService,
    private messageUtil: MessageUtil
  ) {
    this.selectedCountry = this.countryCodes[0];

    this.initializeForm();
    this.loadStates('Brazil');

    if (this.config && this.config.data) {
      if (this.config.data.customer && this.config.data.customer.id) {
        this.isEditMode = true;
        this.customerId = this.config.data.customer.id;
      }
    }
  }


  async ngOnInit(): Promise<void> {
    await Promise.all([this.getWorkshopConfig(), this.getYatchClubConfig()]);

    forkJoin([
      this.getAccessGroupOptions(),
      this.loadGroupActive(),
    ]).subscribe({
      next: ([accessGroupsResult, groupsResult]) => {
        this.getCustomerProfiles();
        this.updateCustomerProfileValidators();
        if (this.isEditMode && this.customerId) {
          this.loadCustomerData();
        }

        this.cdRef.detectChanges();
      }
    });

    this.customerForm.get('email').valueChanges.subscribe(value => {
      if (JSON.stringify(value) !== JSON.stringify(this.emailValues)) {
        this.emailValues = value || [];
      }
    });

    this.customerForm.get('emailCollection').valueChanges.subscribe(value => {
      if (JSON.stringify(value) !== JSON.stringify(this.emailCollectionValues)) {
        this.emailCollectionValues = value || [];
      }
    });
    this.customerForm.get('federalIdType').valueChanges.subscribe(value => {
      this.updateFederalIdValidation();
    });
    this.customerForm.get('federalId').valueChanges.subscribe(value => {

      // CNPJ pode conter letras (A-Z): converte automaticamente para maiusculas.
      if (this.isBrazilianNationality
        && this.customerForm.get('federalIdType').value === 'Juridical'
        && typeof value === 'string') {
        const upper = value.toUpperCase();
        if (upper !== value) {
          this.customerForm.get('federalId').setValue(upper, { emitEvent: false });
        }
      }

      if (!this.isBrazilianNationality) {
        const federalIdControl = this.customerForm.get('federalId');
        if (value && value.trim() !== '') {
          federalIdControl.setErrors(null);
        }
      }
    });

  }

  loadCustomerData(): void {
    this.spinner.show();
    this.customerService.getCustomerById(this.customerId).subscribe({
      next: (response) => {
        this.customerData = response;
        this.populateFormWithCustomerData();
        this.spinner.hide();
      },
      error: (error) => {
        console.error('Erro ao carregar dados do cliente:', error);
        this.messageUtil.generateMessage('error', 'Erro', 'Não foi possível carregar os dados do cliente');
        this.spinner.hide();
      }
    });
  }


populateFormWithCustomerData(): void {
  if (!this.customerData) return;

  const customer = this.customerData;

  const nationality = customer.documentCountry || customer.nationality || 'BR';
  this.isBrazilianNationality = nationality === 'BR';

  if (this.isBrazilianNationality) {
    this.federalIdTypeOptions = [
      { label: 'Pessoa Física', value: 'Physical' },
      { label: 'Pessoa Jurídica', value: 'Juridical' }
    ];
  } else {
    this.federalIdTypeOptions = [ { label: 'Estrangeiro', value: 'Foreigner' } ];
  }

  let customerProfileValue = null;
  if (customer.customerProfile) {
    switch (customer.customerProfile) {
      case 'MARINE':
        customerProfileValue = CustomerProfile.Marine;
        break;
      case 'WORKSHOP':
        customerProfileValue = CustomerProfile.Workshop;
        break;
      case 'WORKSHOP_AND_MARINE':
        customerProfileValue = CustomerProfile.MarineAndWorkshop;
        break;
    }
  }

  if (customer.accessGroupId) {
    const accessGroupId = Number(customer.accessGroupId);

    const foundGroup = this.accessGroupOptions.find(option =>
      option.value && option.value.id === accessGroupId
    );

    if (foundGroup) {
      this.customerForm.get('accessGroup').setValue(foundGroup.value);
    } else {
      console.warn('Grupo de acesso não encontrado para o ID:', accessGroupId);
      this.customerForm.get('accessGroup').setValue(null);
    }
  }

  const addressCountry = customer.address?.country || 'BR';
  this.isBrazilianAddress = addressCountry === 'BR';

  if (this.isBrazilianAddress) {
    this.loadStates('Brazil');
    if (customer.address?.state) {
      this.loadCities();
    }
  }

  if (customer.billingEmails && customer.billingEmails.length > 0) {
    this.useEmailCollection = true;
    this.emailCollectionValues = [...customer.billingEmails];
  }

  let selectedAccessGroup = null;
  if (customer.accessGroupId) {
    const foundGroup = this.accessGroupOptions.find(group =>
      group.value && group.value.id === customer.accessGroupId
    );
    if (foundGroup) {
      selectedAccessGroup = foundGroup.value;
    }
  }



  let billingDate = null;
  const [year, month, day] = customer.billingDay.split('-').map(Number);
  billingDate = new Date(year, month - 1, day);

  const preferredPhoneCountry = this.resolvePreferredPhoneCountryCode(customer);

  if (customer.phone) {
    this.parsePhoneNumber(customer.phone, preferredPhoneCountry);
  }

  const parsedCountry = this.customerForm.get('phoneCountry').value as CountryCode;
  if (parsedCountry?.code && this.customerData) {
    this.customerData.phoneCountryCode = parsedCountry.code;
  }

  this.imgPreviewPhoto = customer.photoUrl || '';

  this.customerForm.patchValue({
    name: customer.name,
    nationality: nationality,
    federalIdType: customer.federalIdType || 'Physical',
    federalId: customer.federalId,
    email: customer.emails || [],
    emailCollection: customer.billingEmails || [],
    codRegistration: customer.codRegistration || '',
    photoUrl: customer.photoUrl || '',
    customerProfile: this.isWorkshop ? customerProfileValue : null,
    group: customer.billingGroupId,
    billingDay: billingDate,
    dueDate: customer.dueDate,
    accessGroup: selectedAccessGroup,
    address: {
      country: customer.address?.country || 'BR',
      zip: customer.address?.zip || '',
      state: customer.address?.state || '',
      city: customer.address?.city || '',
      district: customer.address?.district || '',
      street: customer.address?.street || '',
      number: customer.address?.number || '',
      complement: customer.address?.complement || ''
    }
  },{ emitEvent: false });

  this.emailValues = customer.emails || [];
  this.emailCollectionValues = customer.billingEmails || [];

  this.updateFederalIdValidation();
  this.updateAddressValidations();

  setTimeout(() => {
    this.cdRef.detectChanges();

  });
}

parsePhoneNumber(phoneString: string, preferredCountryCode?: string): void {
  if (!phoneString) {
    return;
  }

  const numericPhone = phoneString.toString().replace(/\D/g, '');
  if (!numericPhone) {
    return;
  }

  const sortedCountries = [...this.countryCodes].sort(
    (a, b) => normalizeDialCode(b.dialCode).length - normalizeDialCode(a.dialCode).length
  );

  let matchedCountry: CountryCode | undefined;
  let dialCodeDigits = '';

  const preferredCountry = preferredCountryCode
    ? this.countryCodes.find(
        (country) => country.code === preferredCountryCode
      )
    : undefined;

  if (preferredCountry) {
    const preferredDial = normalizeDialCode(preferredCountry.dialCode);
    if (preferredDial && numericPhone.startsWith(preferredDial)) {
      matchedCountry = preferredCountry;
      dialCodeDigits = preferredDial;
    }
  }

  if (!matchedCountry) {
    for (const country of sortedCountries) {
      const candidateDialCode = normalizeDialCode(country.dialCode);
      if (candidateDialCode && numericPhone.startsWith(candidateDialCode)) {
        matchedCountry = country;
        dialCodeDigits = candidateDialCode;
        break;
      }
    }
  }

  if (!matchedCountry) {
    return;
  }

  const shouldApplyPriority =
    !preferredCountry || matchedCountry.code !== preferredCountry.code;

  if (shouldApplyPriority) {
    const prioritizedCountry = findCountryByDialCode(`+${dialCodeDigits}`);
    if (prioritizedCountry) {
      matchedCountry = prioritizedCountry;
      dialCodeDigits = normalizeDialCode(prioritizedCountry.dialCode);
    }
  }

  const remainingDigits = numericPhone.substring(dialCodeDigits.length);
  const formattedNumber = this.formatPhoneNumberForCountry(remainingDigits, matchedCountry);

  this.customerForm.get('phoneCountry').setValue(matchedCountry);
  this.selectedCountry = matchedCountry;
  this.customerForm.get('phoneNumber').setValue(formattedNumber);
  this.customerForm.get('phoneNumber').updateValueAndValidity();
}

  ngAfterViewInit() {
    setTimeout(() => {
      this.cdRef.detectChanges();
    });
  }

  async getWorkshopConfig() {
    this.isWorkshop = await this.operationalConfigService.isWorkshop();
    this.updateCustomerProfileValidators();
  }

  shouldShowError(controlName: string, errorType: string): boolean {
    const control = this.customerForm.get(controlName);

    if (controlName === 'federalId' && !this.isBrazilianNationality && errorType === 'required') {
      return (this.submitted || control.touched) &&
             (!control.value || control.value.trim() === '') &&
             control.hasError(errorType);
    }

    return (this.submitted || control.touched) && control.hasError(errorType);
  }

onNationalityChange(): void {
  const nationality = this.customerForm.get('nationality').value;
  this.isBrazilianNationality = nationality === 'BR';

  if (this.isBrazilianNationality) {
    this.federalIdTypeOptions = [
      { label: 'Pessoa Física', value: 'Physical' },
      { label: 'Pessoa Jurídica', value: 'Juridical' }
    ];
    this.customerForm.get('federalIdType').setValue('Physical');
  } else {
    this.federalIdTypeOptions = [ { label: 'Estrangeiro', value: 'Foreigner' } ];
    this.customerForm.get('federalIdType').setValue('Foreigner');
  }

  this.updateFederalIdValidation();
  this.cdRef.detectChanges();
}

onAddressCountryChange(event: any): void {
  const selectedCountry = event.value;
  this.isBrazilianAddress = selectedCountry === 'BR';

 const addressForm = this.customerForm.get('address') as FormGroup;
  addressForm.get('zip').setValue('');
  addressForm.get('state').setValue('');
  addressForm.get('city').setValue('');

  this.updateAddressValidations();

  if (this.isBrazilianAddress) {
      this.loadStates('Brazil');
  } else {
      this.states = [];
  }
}

  private loadGroupActive(): Observable<void> {
    return new Observable<void>((observer) => {
      this.groupService.findAllActive().subscribe({
        next: (data) => {
          this.groups = [{ label: 'Selecione grupo de faturamento', value: null }].concat(
            data.map((g) => ({ label: g.name, value: g.id }))
          );

          observer.next();
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        },
      });
    });
  }

  getCustomerProfiles() {
    this.customerProfile = [
      { label: 'Selecione o perfil de cliente', value: null },
      { label: 'Marina', value: CustomerProfile.Marine },
      this.isWorkshop ? { label: 'Oficina', value: CustomerProfile.Workshop } : null,
      this.isWorkshop ? { label: 'Oficina e Marina', value: CustomerProfile.MarineAndWorkshop } : null
    ].filter(item => item !== null);
  }



  private getAccessGroupOptions(): Observable<void> {
    return new Observable<void>((observer) => {
      this.accessCategoryService.findAll().subscribe({
        next: (data) => {
          this.accessGroupOptions = data
            .map(
              (ag) =>
                <SelectItem>{
                  label: ag.name,
                  value: ag,
                }
            )
            .filter(
              (ag) =>
                ag.value.active === true &&
                (ag.value.type === AccessGroupEnum.NON_MEMBER ||
                  ag.value.type === AccessGroupEnum.OWNER)
            );
          observer.next();
          observer.complete();
        },
        error: (error) => {
          console.error('Error in accessCategoryService.findAll:', error);
          observer.error(error);
        },
      });

    });

  }

  initializeForm(): void {
    const initialNationality = 'BR';
    const initialFederalIdType = initialNationality === 'BR' ? 'Physical' : 'Foreigner';

    this.customerForm = new FormGroup({
      accessGroup: new FormControl(null, Validators.required),
      name: new FormControl('', Validators.required),
      nationality: new FormControl(initialNationality, Validators.required),
      federalIdType: new FormControl(initialFederalIdType, Validators.required),
      federalId: new FormControl('', [this.isBrazilianNationality ? Validators.required : null]),
      email: new FormControl([], [Validators.required, this.validateEmails]),
      emailCollection: new FormControl([]),
      photoUrl: new FormControl(''),
      phoneCountry: new FormControl(this.selectedCountry, Validators.required),
      phoneNumber: new FormControl('', [Validators.required, this.validatePhoneNumber.bind(this)]),
      codRegistration: new FormControl(''),
      customerProfile: new FormControl(null),
      group: new FormControl(null, Validators.required),
      billingDay: new FormControl(null, Validators.required),
      dueDate: new FormControl(null, Validators.required),
      address: new FormGroup({
        country: new FormControl('BR', Validators.required),
        zip: new FormControl('', Validators.required),
        state: new FormControl(''),
        city: new FormControl('', Validators.required),
        street: new FormControl('', Validators.required),
        number: new FormControl('', Validators.required),
        district: new FormControl('', Validators.required),
        complement: new FormControl(''),
      })
    });
    this.updateFederalIdValidation();
    this.updateAddressValidations();
  }

  private updateCustomerProfileValidators(): void {
    const control = this.customerForm.get('customerProfile');
    if (this.isWorkshop) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
      control.setValue(null);
    }
    control.updateValueAndValidity();
  }

  private updateAddressValidations(): void {
    const stateControl = this.customerForm.get('address.state');

    if (this.isBrazilianAddress) {
      stateControl.setValidators([Validators.required]);
    } else {
      stateControl.clearValidators();
    }

    stateControl.updateValueAndValidity();
  }


filterCountries(event: Event): void {
  const value = (event.target as HTMLInputElement).value.toLowerCase();
  this.filteredCountries = of(this.countryCodes.filter(country =>
    country.name.toLowerCase().includes(value) ||
    country.dialCode.includes(value)
  ));
}

onCountrySelect(country: CountryCode): void {
  this.selectedCountry = country;
  this.customerForm.get('phoneCountry').setValue(country);
  this.customerForm.get('phoneNumber').updateValueAndValidity();
}

validatePhoneNumber(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return { required: true };

  const selectedCountry = this.customerForm.get('phoneCountry').value;

  if (selectedCountry.code === 'BR') {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length < 10 || digitsOnly.length > 11) {
      return { invalidPhoneNumber: true };
    }
  } else if (selectedCountry.code === 'US' || selectedCountry.code === 'UM') {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length !== 10) {
      return { invalidPhoneNumber: true };
    }
  } else if (selectedCountry.code === 'PT') {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length !== 9) {
      return { invalidPhoneNumber: true };
    }
  }
  return null;
}

applyPhoneMask(event: any): void {
  const formattedValue = this.formatPhoneNumberForCountry(event.target.value, this.selectedCountry);
  event.target.value = formattedValue;
  this.customerForm.get('phoneNumber').setValue(formattedValue, { emitEvent: false });
}

private formatPhoneNumberForCountry(rawValue: string, country: CountryCode): string {
  const digits = (rawValue ?? '').toString().replace(/\D/g, '');

  if (!digits) {
    return '';
  }

  if (!country) {
    return digits;
  }

  if (country.code === 'BR') {
    if (digits.length > 10) {
      return digits.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3');
    }
    if (digits.length > 6) {
      return digits.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3');
    }
    if (digits.length > 2) {
      return digits.replace(/^(\d{2})(\d{0,5}).*/, '($1) $2');
    }
    return digits;
  }

  if (country.code === 'US' || country.code === 'UM') {
    if (digits.length > 6) {
      return digits.replace(/^(\d{3})(\d{3})(\d{0,4}).*/, '($1) $2-$3');
    }
    if (digits.length > 3) {
      return digits.replace(/^(\d{3})(\d{0,3}).*/, '($1) $2');
    }
    return digits;
  }

  if (country.code === 'PT') {
    if (digits.length <= 2) {
      return digits;
    }

    if (digits.length > 4) {
      return digits.replace(/^(\d{2})(\d{3})(\d{0,4}).*/, (match, p1, p2, p3) => {
        return [p1, p2, p3].filter(Boolean).join(' ');
      });
    }

    return digits.replace(/^(\d{2})(\d{0,3}).*/, (match, p1, p2) => {
      return [p1, p2].filter(Boolean).join(' ');
    });
  }

  return digits;
}


  private resolvePreferredPhoneCountryCode(customer: any): string | null {
    if (!customer) {
      return null;
    }

    const possibleKeys = [
      'phoneCountryCode',
      'phoneCountry',
      'phoneCountryIso',
      'phoneCountryIso2',
      'phoneCountryIso3',
      'phoneCountryISO',
      'phoneCountryISO2',
      'phoneCountryISO3'
    ];

    for (const key of possibleKeys) {
      const value = customer[key];
      if (!value) {
        continue;
      }

      if (typeof value === 'string') {
        const normalized = this.resolveCountryCodeFromString(value);
        if (normalized) {
          return normalized;
        }
      }

      if (typeof value === 'object') {
        const candidates = [
          value?.code,
          value?.iso2,
          value?.iso,
          value?.isoCode,
          value?.countryCode,
          value?.alpha2,
          value?.alpha2Code,
          value?.value,
          value?.name,
          value?.description,
          value?.label
        ];

        for (const candidate of candidates) {
          if (typeof candidate === 'string') {
            const normalized = this.resolveCountryCodeFromString(candidate);
            if (normalized) {
              return normalized;
            }
          }
        }
      }
    }

    if (customer.address?.country === 'UM' || customer.nationality === 'UM') {
      return 'UM';
    }

    return null;
  }

  private resolveCountryCodeFromString(rawValue: string): string | null {
    if (!rawValue) {
      return null;
    }

    const trimmed = rawValue.trim();
    if (!trimmed) {
      return null;
    }

    const upperValue = trimmed.toUpperCase();

    const byCode = this.countryCodes.find(country => country.code.toUpperCase() === upperValue);
    if (byCode) {
      return byCode.code;
    }

    const numericDial = trimmed.replace(/\D/g, '');
    if (numericDial) {
      const byDial = this.countryCodes.find(country => normalizeDialCode(country.dialCode) === numericDial);
      if (byDial) {
        return byDial.code;
      }
    }

    const normalizeName = (value: string) =>
      FormatUtil.getNotAccents(value || '').toUpperCase();

    const normalizedValue = normalizeName(trimmed);
    const byName = this.countryCodes.find(country => normalizeName(country.name) === normalizedValue);
    if (byName) {
      return byName.code;
    }

    return null;
  }



  validateEmails = (control: AbstractControl) => {
    const emails = control.value;
    if (!emails || !Array.isArray(emails)) {
      return { required: true };
    }

    if (emails.length === 0) {
      return { required: true };
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const invalid = emails.some(email => !emailRegex.test(email.trim()));

    return invalid ? { invalidEmail: true } : null;
  }

  toggleEmailCollection(): void {
    const emailCollectionControl = this.customerForm.get('emailCollection');

    if (this.useEmailCollection) {
      emailCollectionControl.setValidators([Validators.required, this.validateEmails]);
    } else {
      emailCollectionControl.clearValidators();
      emailCollectionControl.setValue([]); 
      emailCollectionControl.markAsPristine();
      emailCollectionControl.markAsUntouched();
      emailCollectionControl.setErrors(null);
    }

    emailCollectionControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    this.cdRef.detectChanges();
  }



  private updateFederalIdValidation(): void {
    const federalIdControl = this.customerForm.get('federalId');
    federalIdControl.clearValidators();

    if (this.isBrazilianNationality) {
      federalIdControl.setValidators([
        Validators.required,
        this.validateDocument.bind(this)
      ]);
    } else {
      federalIdControl.setValidators([Validators.required]);
    }

    federalIdControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }


  validateDocument = (control: FormControl) => {
    if (!this.isBrazilianNationality) return null;

    const value = control.value;
    if (!value) return { required: true };

    if (this.customerForm.get('federalIdType').value === 'Physical') {
      const rawValue = value.replace(/\D/g, '');
      if (rawValue.length !== 11) return { invalidFederalId: true };
      return this.validateCPF(rawValue) ? null : { invalidFederalId: true };
    } else if (this.customerForm.get('federalIdType').value === 'Juridical') {
      // CNPJ pode ser alfanumerico: preserva letras (A-Z) e nao apenas digitos.
      const rawValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      if (rawValue.length !== 14) return { invalidFederalId: true };
      return this.validateCNPJ(rawValue) ? null : { invalidFederalId: true };
    }

    return null;
  }

private validateCNPJ(cnpj: string): boolean {
  if(!this.isBrazilianNationality) { true }
  cnpj = cnpj.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (cnpj.length !== 14) return false;

  // CNPJ alfanumerico (contem letras): valida apenas formato (12 alfanum + 2 DV numericos), sem DV.
  if (/[A-Z]/.test(cnpj)) {
    return /^[A-Z0-9]{12}\d{2}$/.test(cnpj);
  }

  let size = cnpj.length - 2;
  let numbers = cnpj.substring(0, size);
  const digits = cnpj.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - sum % 11;
  if (result !== parseInt(digits.charAt(0))) return false;

  size = size + 1;
  numbers = cnpj.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - sum % 11;
  if (result !== parseInt(digits.charAt(1))) return false;

  return true;
}

private validateCPF(cpf: string): boolean {
  if(!this.isBrazilianNationality) { true }
  cpf = cpf.replace(/\D/g, '');
  if (cpf.length !== 11) return false;

  let sum = 0;
  let remainder: number;

  for (let i = 1; i <= 9; i++)
    sum += parseInt(cpf.substring(i-1, i)) * (11 - i);
  remainder = (sum * 10) % 11;

  if ((remainder === 10) || (remainder === 11))
    remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10)))
    return false;

  sum = 0;
  for (let i = 1; i <= 10; i++)
    sum += parseInt(cpf.substring(i-1, i)) * (12 - i);
  remainder = (sum * 10) % 11;

  if ((remainder === 10) || (remainder === 11))
    remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11)))
    return false;

  return true;
}

  loadStates(countryId): void {
    this.states = [{ label: 'Selecione a Unidade Federativa', value: null }].concat(
      this.locationService
        .getStates()
        .map((s) => ({ label: s.name, value: s.id }))
    );
  }

  loadCities(): void {
    if (!this.isBrazilianNationality ) {
      this.filteredCities = [];
      return;
    }

    const country = this.customerForm.get('address.country').value;
    const state = this.customerForm.get('address.state').value;
    this.locationService.getCities(country, state).subscribe((cities: City[]) => {
      this.filteredCities = cities.map(city => city.name);
    });
  }


getCep(): void {
  if (!this.isBrazilianAddress ) return;

  const zip = this.customerForm.get('address.zip').value?.replace(/\D/g, '');
  if (zip?.length === 8) {
    this.locationService.getCep(zip).subscribe({
      next: (data) => {
        if (data) {
          this.customerForm.patchValue({
            address: {
              street: data.logradouro || '',
              district: data.bairro || '',
              city: data.cidade || '',
              state: data.uf || ''
            }
          });
        }
      },
      error: () => {
        if (this.submitted && !this.customerForm.get('address.zip').value) {
          this.customerForm.get('address.zip').setErrors({ required: true });
        }
      }
    });
  } else if (this.submitted && !zip) {
    this.customerForm.get('address.zip').setErrors({ required: true });
  }
}

  filterCities(event): void {
    this.filteredCities = this.filteredCities.filter(c =>
      c.toLowerCase().includes(event.query.toLowerCase())
    );
  }





  updateEmailFormControl() {
    this.customerForm.get('email').setValue([...this.emailValues]);
  }

  updateEmailCollectionFormControl() {
    if (this.useEmailCollection) {
      this.customerForm.get('emailCollection').setValue([...this.emailCollectionValues]);
    } else {
      this.customerForm.get('emailCollection').setValue([]);
    }
  }

  onEmailAdd(event: any, controlName: string): void {
    const control = this.customerForm.get(controlName);
    const currentEmails = [...control.value];

    const newEmail = event.value.trim();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (emailRegex.test(newEmail) && !currentEmails.includes(newEmail)) {
      currentEmails.push(newEmail);
      control.setValue(currentEmails);
    }

    this.cdRef.detectChanges();
  }

  onEmailRemove(event: any, controlName: string): void {
    const control = this.customerForm.get(controlName);
    const currentEmails = [...control.value];
    const index = currentEmails.indexOf(event.value);

    if (index !== -1) {
      currentEmails.splice(index, 1);
      control.setValue(currentEmails);
    }

    this.cdRef.detectChanges();
  }

  getErrorTooltip(control: any): string {
    if (!control || !control.errors || !this.submitted) return '';
    return Object.keys(control.errors).map(error => {
      switch (error) {
        case 'required': return 'Campo obrigatório';
        case 'email': return 'E-mail inválido';
        case 'invalidFederalId': return 'Documento inválido';
        case 'validatePhoneNumber': return 'Número de telefone inválido';
        default: return '';
      }
    }).join('\n');
  }

  validateForeignDocument(): void {
    if (!this.isBrazilianNationality) {
      const federalIdControl = this.customerForm.get('federalId');
      const value = federalIdControl.value;

      if (value && value.trim() !== '') {
        federalIdControl.setErrors(null);
        federalIdControl.updateValueAndValidity({ emitEvent: false });
      } else {
        federalIdControl.setErrors({ required: true });
      }

      this.cdRef.detectChanges();
    }
  }

  save(event: Event): void {
    event.preventDefault();

    this.submitted = true;
    this.updateCustomerProfileValidators();

    this.markAllAsTouched(this.customerForm);

  if (!this.isWorkshop) {
    this.customerForm.get('customerProfile').setErrors(null);
  }


    if (!this.useEmailCollection) {
      const emailCollectionControl = this.customerForm.get('emailCollection');
      emailCollectionControl.clearValidators();
      emailCollectionControl.updateValueAndValidity({ emitEvent: false });
      emailCollectionControl.setErrors(null);
    }

    if (this.customerForm.invalid) {
      console.error('Formulário inválido:', this.customerForm);
      Object.keys(this.customerForm.controls).forEach(key => {
        const control = this.customerForm.get(key);
        if (control && control.invalid) {
          console.error(`Control "${key}" is invalid:`, control.errors);
        }
      });
      return;
    }

    const formValue = this.customerForm.value;
    const selectedPhoneCountry = formValue.phoneCountry as CountryCode;

    let federalIdForPayload: string;
    if (!this.isBrazilianNationality) {
      federalIdForPayload = formValue.federalId; // Mantém o valor original
    } else if (formValue.federalIdType === 'Juridical') {
      // CNPJ pode ser alfanumerico: preserva letras (A-Z), removendo apenas a mascara.
      federalIdForPayload = formValue.federalId.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    } else {
      federalIdForPayload = formValue.federalId.replace(/\D/g, '');
    }

    const payload: CustomerPayload = {
      name: formValue.name,
      nationality: formValue.nationality,
      accessGroupId: formValue.accessGroup?.id,
      customerProfileId: this.isWorkshop ? formValue.customerProfile : null,
      workshop: Boolean(this.isWorkshop),
      federalId: federalIdForPayload,
      federalIdType: formValue.federalIdType,
      emails: formValue.email || [],
      billingEmails: this.useEmailCollection ? (formValue.emailCollection || []) : [],
      billingGroupId: formValue.group,
      billingStartDate: this.formatDate(formValue.billingDay),
      dueDate: formValue.dueDate,
      phone: this.formatPhoneNumber(),
      phoneCountryCode: selectedPhoneCountry ? selectedPhoneCountry.code : null,
      codRegistration: this.isYatchClub
        ? ((formValue.codRegistration || '').trim() || null)
        : (this.customerData?.codRegistration ?? null),
      // EM-2936: o PUT grava photo_url sempre; sem o campo, a edição apagava a foto do cliente.
      photoUrl: this.getPhotoUrlForPayload(),
      address: {
        country: formValue.address.country,
        zip: formValue.address.zip.replace(/\D/g, ''),
        state: formValue.address.state,
        city: formValue.address.city,
        district: formValue.address.district,
        street: formValue.address.street,
        number: formValue.address.number,
        complement: formValue.address.complement || 'Não informado',
        addressId: this.isEditMode && this.customerData?.address?.addressId ? this.customerData.address.addressId : null
      }
    };

    this.spinner.show();

    if (this.isEditMode && this.customerId) {
      this.customerService.put(
        this.customerId.toString(),
        payload
      ).subscribe({
        next: (responseText) => {

          this.messageUtil.generateMessage('success', 'Sucesso', 'Cliente atualizado com sucesso');
          this.spinner.hide();
          this.dialogRef.close(true);
        },
        error: (error) => {
          console.error('Erro ao atualizar cliente:', error);
           const errorMessage = typeof error.error === 'string' ? error.error : 'Não foi possível atualizar o cliente';
          this.messageUtil.generateMessage('error', 'Erro', errorMessage);
          this.spinner.hide();
        }
      });
    } else {
      this.customerService.createCustomerBasicInfo(payload).subscribe({
        next: (responseText) => {

          this.messageUtil.generateMessage('success', 'Sucesso', 'Cliente criado com sucesso');
          this.spinner.hide();
          this.dialogRef.close(true);
        },
        error: (error) => {
          console.error('Erro ao criar cliente:', error);
          const errorMessage = typeof error.error === 'string' ? error.error : 'Não foi possível criar o cliente';
          this.messageUtil.generateMessage('error', 'Erro', errorMessage);
          this.spinner.hide();
        }
      });
    }


  }

  formatPhoneNumber(): string {
    const country = this.customerForm.get('phoneCountry').value as CountryCode;
    const number = this.customerForm.get('phoneNumber').value;

    if (!country || !number) return '';

    const sanitizedNumber = number.toString().replace(/\D/g, '');
    return `${country.dialCode}${sanitizedNumber}`;
  }



  private formatDate(date: Date): string {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  }

  prepareCustomerData() {
    const formValue = {...this.customerForm.value};

    if (formValue.phone && typeof formValue.phone === 'object') {
      formValue.phone = formValue.phone.internationalNumber;
    }

    return formValue;
  }

  private markAllAsTouched(control: AbstractControl): void {
    if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach(key => {
            const childControl = control.get(key);
            if (childControl instanceof FormGroup || childControl instanceof FormArray) {
                this.markAllAsTouched(childControl);
            } else {
                childControl.markAsTouched();
                childControl.updateValueAndValidity();
            }
        });
    } else if (control instanceof FormArray) {
        control.controls.forEach(arrayControl => {
            this.markAllAsTouched(arrayControl);
        });
    }
  }

  receiveUploadUrlPhoto(url: string): void {
    this.imgPreviewPhoto = url;
    const control = this.customerForm.get('photoUrl');
    if (control) {
      control.setValue(url || '');
      control.markAsDirty();
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  /** Foto enviada agora, se houver; senão a que já estava gravada (nula quando o cliente não tem foto). */
  private getPhotoUrlForPayload(): string | null {
    const formPhotoUrl = this.customerForm.get('photoUrl').value;
    if (formPhotoUrl && formPhotoUrl !== this.customerData?.photoUrl) {
      return formPhotoUrl;
    }
    return this.customerData?.photoUrl || null;
  }

  validateFederalId(): void {
    const federalIdControl = this.customerForm.get('federalId');
    if (federalIdControl) {
      federalIdControl.updateValueAndValidity();
    }
  }

  validateRequiredFields(): void {
    const requiredFields = [
      'accessGroup',
      'name',
      'nationality',
      'federalIdType',
      'federalId',
      'email',
      'emailCollection',
      'phone',
      'customerProfile',
      'group',
      'billingDay',
      'dueDate'
    ];

    const requiredAddressFields = [
      'country',
      'zip',
      'state',
      'city',
      'street',
      'number',
      'district'
    ];

    requiredFields.forEach(field => {
      const control = this.customerForm.get(field);
      if (control) {
        control.setValidators([Validators.required]);
        control.updateValueAndValidity();
      }
    });

    requiredAddressFields.forEach(field => {
      const control = this.customerForm.get('address').get(field);
      if (control) {
        control.setValidators([Validators.required]);
        control.updateValueAndValidity();
      }
    });
  }

  async getYatchClubConfig() {
    this.isYatchClub = await this.operationalConfigService.isYatchClub();
  }

}
